import { z } from "zod";

import {
  WHATSAPP_MESSAGE_MAX,
  WHATSAPP_NUMBER_PATTERN,
  WHATSAPP_TOKEN_DATE,
  WHATSAPP_TOKEN_TOUR_NAME,
} from "../apps/whatsapp-widget";

/** Four keeps the assurance strip to one readable row on a phone. */
export const TOUR_PAGE_MAX_TRUST_BADGES = 4;
/** Six is the most a departure panel carries before it buries the dates. */
export const TOUR_PAGE_MAX_DEPARTURE_DETAILS = 6;
/** Also `RecommendedOnCard.tsx`'s input placeholder — one constant so the two
 *  can't drift apart. */
export const TOUR_PAGE_DEFAULT_RECOMMENDED_ON = "Recommended on Tripadvisor";

/**
 * An icon name from the shared `TRAVEL_ICONS` registry.
 *
 * Validated as a non-empty string rather than an enum of all 431 names, matching
 * `facet-type.dto.ts`. The registry is what the operator picker offers, and it is
 * the only vocabulary this field is ever written from.
 *
 * Nothing renders this field on the storefront yet. When that lands it should
 * follow the fallback `_info-chip-icon.server.tsx` already uses for facet-type
 * chips — resolve the name against the registry's path map, and draw the
 * surface's own glyph when it is absent.
 */
const iconName = z.string().trim().min(1, "Please choose an icon");

export const tourPageTrustBadgeSchema = z.object({
  icon: iconName,
  title: z.string().trim().min(1, "Give the badge a title").max(40),
  description: z.string().trim().max(80).default(""),
});
export type TourPageTrustBadge = z.infer<typeof tourPageTrustBadgeSchema>;

/**
 * What a tour page shows before its operator has written anything.
 *
 * Every `icon` is a name in `TRAVEL_ICONS`, checked against the registry by
 * this module's own spec. An unknown name does NOT draw nothing — it draws
 * `CircleCheckIcon` (`_tour-page-icon.server.tsx:60`), so a typo ships a
 * plausible-looking wrong glyph rather than an obvious blank. That is exactly
 * why the spec exists; this line claimed "no glyph" until 2026-10-26 and
 * would have had a reader looking for an empty box.
 *
 * NONE of them asserts a fact about the operator's business. They shipped on
 * 2026-10-26 promising a 60-day refund window and a 4.9 rating, both invented
 * on behalf of agencies that had never opened the screen; the wording now
 * points at where the real terms appear instead of inventing them. Keep any
 * new default to that test: would it still be true of a brand-new tour with
 * no policy, no reviews and no departures?
 *
 * These are OUR words, not the operator's, so the storefront renders them
 * through `t()` (`SEEDED_BADGE_COPY` in `_hero/hero.server.tsx`) rather than
 * printing these strings. The English here is what the operator sees in their
 * settings form and what gets stored if they save without editing.
 */
export const TOUR_PAGE_DEFAULT_TRUST_BADGES: readonly TourPageTrustBadge[] = [
  {
    icon: "shield",
    title: "Licensed Operator",
    description: "Registered operator with local permits handled for you.",
  },
  // Was "Flexible cancellation / Free cancellation up to 60 days before
  // departure." That is a refund window a traveller plans around, promised on
  // behalf of an operator who never agreed to it — the exact assertion #791
  // removed. Points at the terms instead of inventing them.
  {
    icon: "calendar-x",
    title: "Cancellation terms",
    description: "Shown in full before you pay.",
  },
  // `credit-card` and `star` are the two SLOTS the storefront swaps a real
  // fact into (`buildAssurances`), so their seeded copy is only ever read
  // when that fact is ABSENT. It therefore has to be true of a tour with no
  // deposit policy and no reviews — which is why neither states one.
  {
    icon: "credit-card",
    title: "Payment options",
    description: "Shown at checkout before you confirm.",
  },
  // Was "Rated 4.9/5" — a score we invented, shown precisely on the tours
  // that have no rating at all.
  {
    icon: "star",
    title: "Traveler reviews",
    description: "See what past travelers say.",
  },
];

export const tourPageDepartureDetailSchema = z.object({
  icon: iconName,
  text: z.string().trim().min(1, "Write what travelers should know").max(80),
});
export type TourPageDepartureDetail = z.infer<
  typeof tourPageDepartureDetailSchema
>;

const awardSchema = z.object({
  enabled: z.boolean().default(false),
  /**
   * A public https URL from the agency's own media library.
   *
   * HTTPS only. `http://` was accepted until 2026-09-17 and rendered as an
   * empty box: traveler-web's CSP is `img-src 'self' blob: data: https:`, so
   * the browser refuses the request and nothing tells the operator why. Costs
   * nothing in practice — the Choose Image flow only ever yields an https
   * media-library URL.
   */
  imageUrl: z
    .string()
    .trim()
    .url("Pick an image from your media library")
    .max(2048)
    .refine((u) => /^https:\/\//i.test(u), {
      message: "Use an https:// image URL",
    })
    .optional()
    // `.catch(undefined)` — an unacceptable URL drops the SEAL, never the
    // whole object.
    //
    // Both readers parse the ENTIRE settings blob and fall back hard on
    // failure: `GetTourPageSettingsService` returns
    // `tourPageSettingsSchema.parse({})`, `TourPageReader.getView` returns
    // null. Without this catch, one bad field blanks the operator's whole
    // screen — badges, recommendation, WhatsApp, all of it — with no message,
    // and the next Save writes those defaults over their real row while the
    // storefront loses every surface at once.
    //
    // Not hypothetical: tightening this refine from `https?` to `https` on
    // 2026-09-17 made exactly that true for any agency who had saved an http
    // URL while the looser rule was live.
    //
    // The trade, stated plainly: `.catch` runs on EVERY parse, operator-api's
    // write validation included. So a hand-crafted PUT carrying an http URL
    // does not 422 — it saves with the seal silently dropped. That is the
    // right end of the trade because the only way to reach it is by bypassing
    // the settings screen, whose picker yields https media-library URLs and
    // nothing else; and losing one decorative seal beats blanking an agency's
    // configuration. If this field ever becomes operator-typed, it needs a
    // strict write schema instead of this catch.
    .catch(undefined),
  title: z.string().trim().max(40).default(""),
  tagline: z.string().trim().max(40).default(""),
});

/**
 * The greeting a traveler sends when they tap either departure affordance.
 *
 * Uses the WhatsApp Widget app's OWN token spelling — `{tour_name}`, not the
 * `{tour name}` the design draws. `resolveWhatsappMessage` is the single
 * resolver for both features and only understands the underscored names, and
 * teaching an operator two spellings of one idea in one product is worse than
 * diverging from the mock by a character. The operator inserts these from
 * buttons rather than typing them.
 */
export const TOUR_PAGE_DEFAULT_WHATSAPP_MESSAGE = `Hi, I would like to join for ${WHATSAPP_TOKEN_TOUR_NAME} for ${WHATSAPP_TOKEN_DATE}. Can you please share the details?`;

const departuresSchema = z.object({
  detailsEnabled: z.boolean().default(false),
  details: z
    .array(tourPageDepartureDetailSchema)
    .max(
      TOUR_PAGE_MAX_DEPARTURE_DETAILS,
      `Up to ${TOUR_PAGE_MAX_DEPARTURE_DETAILS} details`,
    )
    .default([]),

  /**
   * The Tour Page's OWN WhatsApp identity — deliberately not the WhatsApp
   * Widget app's.
   *
   * It was the app's until 2026-09-17: `tour-page.reader.ts` joined
   * `agency_apps` on the `whatsapp-widget` slug and took the first team
   * member's number, on the reasoning that one number is easier to keep
   * current than two. The two are now separate settings by product decision —
   * the floating bubble answers general enquiries, this answers "can I get on
   * THIS departure", and an agency may well route those to different people.
   *
   * Blank is the unconfigured state and is valid: an operator can flip a
   * switch on before they have a number to put in it. Neither departure
   * surface renders without one, because a `wa.me` link to nothing is worse
   * than no link — that rule lives once, in `buildTourPageView`.
   */
  whatsappNumber: z
    .union([
      z.literal(""),
      z
        .string()
        .trim()
        .regex(
          WHATSAPP_NUMBER_PATTERN,
          "Include the country code, digits only — e.g. +12345678900",
        ),
    ])
    .default(""),
  whatsappMessage: z
    .string()
    .trim()
    .max(WHATSAPP_MESSAGE_MAX)
    .default(TOUR_PAGE_DEFAULT_WHATSAPP_MESSAGE),

  /** The icon button beside Book now on every departure row. */
  whatsappButtonEnabled: z.boolean().default(false),

  /** The "Ask on WhatsApp" line inside a departure's expanded details panel.
   *  Independent of the row button: an operator can offer one, both or
   *  neither, which is how the design draws them — two switches, two cards. */
  whatsappContactEnabled: z.boolean().default(false),
  whatsappPromptLabel: z
    .string()
    .trim()
    .max(60)
    .default("Not sure this date works for you?"),
  whatsappLinkLabel: z.string().trim().max(40).default("Ask on WhatsApp"),
});

/**
 * What the operator saves — raw, with the WhatsApp message placeholders still
 * in place. The operator form binds to this and operator-api re-validates it on
 * save. What actually renders is decided by `buildTourPageView`.
 */
export const tourPageSettingsSchema = z.object({
  /** Defaults to the real badge text, by product decision — the badge is
   *  opt-out, not opt-in, so every agency starts showing it. Clearing the
   *  field is how an operator turns it off; there is no separate switch for
   *  one line of text.
   *
   *  A Zod default fires whenever the key is missing from the parsed input,
   *  which includes an EXISTING agency's settings row saved before this
   *  field existed — so this also turns the badge on for an agency that
   *  never touched this field, not only a brand-new one. That is intended:
   *  the only way to end up with a hidden badge is an operator who explicitly
   *  cleared it, and `recommendedOn === ""` is exactly that record. */
  recommendedOn: z
    .string()
    .trim()
    .max(60)
    .default(TOUR_PAGE_DEFAULT_RECOMMENDED_ON),
  /** Seeded, not empty — a product decision (2026-10-26). An agency that has
   *  never opened Tours → Configuration → Tour Page starts with these four,
   *  and they appear in the operator form as editable rows rather than as an
   *  empty list, so the operator can see what their travellers are being told
   *  and change it.
   *
   *  `.prefault` with a FACTORY, not `.default([...])`: a plain default hands
   *  every parse the same array instance, so one caller mutating it would
   *  change the default for every agency after it. The factory builds a fresh
   *  array of fresh objects per parse — the same hazard `award` below avoids.
   *
   *  Clearing every row is still how an operator turns the strip off: the
   *  default fires only when the key is ABSENT, so a saved `[]` stays `[]`.
   *
   *  Note for whoever reads this next: these four REPLACE the two columns
   *  `buildAssurances` derives from real data (the deposit terms and the real
   *  review score), because operator badges win outright there. */
  trustBadges: z
    .array(tourPageTrustBadgeSchema)
    .max(
      TOUR_PAGE_MAX_TRUST_BADGES,
      `Up to ${TOUR_PAGE_MAX_TRUST_BADGES} trust badges`,
    )
    .prefault(() =>
      TOUR_PAGE_DEFAULT_TRUST_BADGES.map((badge) => ({ ...badge })),
    ),
  /**
   * `.prefault({})` rather than `.default({})`: it runs `{}` back through
   * `awardSchema` so every field default applies, and — unlike `.default` —
   * gives every parse its own fresh object. A `.default({})` value is reused
   * across every call, so one caller mutating a defaulted array would corrupt
   * the default for every agency after it.
   */
  award: awardSchema.prefault({}),
  departures: departuresSchema.prefault({}),
  /**
   * The lead form the "Ask a question" button opens on every tour page. Null
   * means the built-in "Ask a question" form. A form deleted since it was
   * picked falls back to the built-in too, so the button never opens nothing.
   */
  askQuestionFormId: z.uuid().nullable().default(null),
  /** The title of the dialog that button opens. Null shows the storefront's
   *  own default, translated into the visitor's language. */
  askQuestionHeading: z
    .string()
    .trim()
    .min(1, "Enter a form heading")
    .max(80, "Form heading must be 80 characters or fewer")
    .nullable()
    .default(null),
});
export type TourPageSettings = z.infer<typeof tourPageSettingsSchema>;
